import { ensureFreshAccessToken } from "@utils/tokenManager";
import { apiRequest } from "./apiClient";

export class ConsultationService {
  constructor(backendUrl) {
    this.backendUrl = backendUrl;
  }

  async fetchConsultations() {
    const result = await apiRequest({
      method: "GET",
      endpoint: "/files/with-transcripts",
    });
    return result.data || []; 
  }

  async fetchTemplates() {
    const result = await apiRequest({
      method: "GET",
      endpoint: "/templates",
    });
    return result.data || [];
  }

  async uploadSegment(
    blob,
    isFinal = false,
    { sessionId, segmentIndex },
    retryCount = 0
  ) {
    const MAX_RETRIES = 2;
    const RETRY_DELAY_MS = 10000;
    try {
      await ensureFreshAccessToken();

      const formData = new FormData();
      formData.append(
        "file",
        blob,
        isFinal
          ? "consultation-final.webm"
          : `consultation-part-${segmentIndex}.webm`
      );
      formData.append("is_final", String(isFinal));
      if (sessionId) formData.append("session_id", sessionId);
      if (segmentIndex !== undefined)
        formData.append("segment_index", String(segmentIndex));
      const response = await fetch(`${this.backendUrl}/transcribe`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const err = new Error(`Upload failed with status ${response.status}`);
        err.status = response.status;
        throw err;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error(
        `❌ Upload attempt ${retryCount + 1} failed:`,
        error.message
      );

      if (retryCount < MAX_RETRIES) {
        console.log(
          `⏳ Retrying in ${RETRY_DELAY_MS / 60000} minutes... (${
            retryCount + 1
          }/${MAX_RETRIES})`
        );
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        return this.uploadSegment(
          blob,
          isFinal,
          { sessionId, segmentIndex },
          retryCount + 1
        );
      } else {
        console.error(
          `🚫 Max retries exceeded (${MAX_RETRIES}) for segment ${segmentIndex}`
        );
        return {
          success: false,
          permanentFailure: true,
          error: `Upload failed after ${MAX_RETRIES} retries: ${error.message}`,
          status: error?.status,
        };
      }
    }
  }

  async saveTranscript(text, recordingId) {
    const result = await apiRequest({
      method: "POST",
      endpoint: "/transcripts",
      body: { text, recording_id: recordingId }
    });
    return result.data;
  }

  async autoFillTemplate(transcription, recordingId, localDateTime, processingMode) {
    const result = await apiRequest({
      method: "POST",
      endpoint: "/autofill-template",
      body: {
        transcription: null,
        recording_id: recordingId,
        local_datetime: localDateTime,
        processing_mode: processingMode,
      },
    });
    return result; 
  }

  async autofillSelectedTemplates(
    selectedTemplateIds,
    recordingId,
    transcription,
    localDateTime
  ) {
    const result = await apiRequest({
      method: "POST",
      endpoint: "/autofill-selected-templates",
      body: {
        selected_template_ids: selectedTemplateIds,
        recording_id: recordingId,
        transcription,
        local_datetime: localDateTime,
      },
    });
    return result;
  }

  async pollAutofillStatus({ jobId, statusUrl }) {
    let delay = 1000;
    const maxDelay = 5000;

    while (true) {
      const endpoint = statusUrl 
        ? statusUrl.replace(this.backendUrl, "").replace(/^\//, "") 
        : `autofill-template/status/${jobId}`;

      const result = await apiRequest({
        method: "GET",
        endpoint: `/${endpoint}`,
      });

      if (result.data?.status === "succeeded") return result.data.result || {};
      if (result.data?.status === "failed") {
        throw new Error(result.data?.error || "Autofill failed");
      }

      await new Promise((r) => setTimeout(r, delay));
      delay = Math.min(maxDelay, Math.floor(delay * 1.5));
    }
  }

  async deleteFile(fileId) {
    const result = await apiRequest({
      method: "GET", 
      endpoint: `/files/${fileId}`,
    });
    return result;
  }
async renameFile(fileId, name) {
    const result = await await apiRequest({
    method: "POST", 
    endpoint: `/files/${fileId}/name`,
    body: name,
  });
   return result;
}
}